import { useNavigate } from "react-router-dom";
import API_CONFIG from "../../config/api";
import React, { useState, useEffect, useRef } from "react";
import authService from "../../services/authService";
import PopupAlert from "../popup/popup-alert";
import PopupShare from "../popup/popup-share";
import PostStatsPanel from "../post/PostStatsPanel";
import { formatPostTimestamp } from "../../utils/dateTime";

function CardFeed({
    mode = 'feed',  // 'feed' | 'post'
    id, title, category, category_name, timestamp, views,
    likes, favoriteCount, hasLiked,
    author, author_id, author_nickname, abstract,
    featured_image, first_image_url, author_profile_image, status,
    isAdmin = false,
    isSelected = false,
    onSelectToggle,
    selectLoading = false,
    space_id, space_title, space_thumbnail, visibility,
    // 저장 관련
    hasSaved = false,
    onSaveToggle,
    // post 모드에서 본문에 이미 대표 이미지(캐러셀 등)가 표시될 때 중복 노출 방지
    hideMedia = false,
    imageFetchPriority,
}) {
    const navigate = useNavigate();
    const isPostMode = mode === 'post';
    const isSpacePost = !!space_id;

    // 좋아요 상태
    const [isLiked, setIsLiked] = useState(hasLiked || false);
    const [likeCount, setLikeCount] = useState(favoriteCount !== undefined ? favoriteCount : (likes || 0));

    // Post 모드 전용 상태
    const [loggedInUserId, setLoggedInUserId] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDeleteResult, setShowDeleteResult] = useState(false);
    const [deleteResultMessage, setDeleteResultMessage] = useState('');
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [likeAuth, setLikeAuth] = useState(() => authService.isLoggedIn());

    // ... 액션 메뉴
    const [showActionsMenu, setShowActionsMenu] = useState(false);
    const [showStatsPanel, setShowStatsPanel] = useState(false);
    const actionsMenuRef = useRef(null);

    // Post 모드: 로그인한 사용자 ID
    useEffect(() => {
        if (isPostMode) {
            const data = authService.getUserData();
            setLoggedInUserId(data.userId || data.id || null);
        }
    }, [isPostMode]);

    // ... 메뉴 외부 클릭 닫기
    useEffect(() => {
        if (!showActionsMenu) return;
        const handler = (e) => {
            if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
                setShowActionsMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showActionsMenu]);

    useEffect(() => {
        if (isPostMode && favoriteCount !== undefined) setLikeCount(favoriteCount);
    }, [favoriteCount, isPostMode]);

    useEffect(() => {
        if (isPostMode && hasLiked !== undefined) setIsLiked(hasLiked);
    }, [hasLiked, isPostMode]);

    useEffect(() => {
        if (!isPostMode) setLikeCount(likes || 0);
    }, [likes, isPostMode]);

    useEffect(() => {
        const onAuth = () => setLikeAuth(authService.isLoggedIn());
        window.addEventListener('userDataChanged', onAuth);
        return () => window.removeEventListener('userDataChanged', onAuth);
    }, []);

    useEffect(() => {
        if (!isPostMode && !likeAuth) { setIsLiked(false); return; }
        if (!isPostMode && id && likeAuth) {
            if (hasLiked !== undefined) { setIsLiked(hasLiked); return; }
            fetch(`/api/has_liked?post_id=${id}`)
                .then(res => res.json())
                .then(data => { if (data.success) setIsLiked(data.hasLiked); })
                .catch(err => console.error("Error fetching like status:", err));
        }
    }, [id, isPostMode, likeAuth, hasLiked]);

    useEffect(() => {
        if (!isPostMode) return;
        const handleLikeUpdate = (event) => {
            if (event.detail.postId === id) {
                setIsLiked(event.detail.isLiked);
                if (typeof event.detail.newFavoriteCount === 'number') {
                    setLikeCount(event.detail.newFavoriteCount);
                } else {
                    setLikeCount(prev => event.detail.isLiked ? prev + 1 : Math.max(0, prev - 1));
                }
            }
        };
        window.addEventListener('likeUpdated', handleLikeUpdate);
        return () => window.removeEventListener('likeUpdated', handleLikeUpdate);
    }, [id, isPostMode]);

    const handleLike = async (e) => {
        e.stopPropagation();
        try {
            const response = await fetch('/api/like_post', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ postId: id })
            });
            const data = await response.json();
            if (data.success) {
                if (isPostMode) {
                    window.dispatchEvent(new CustomEvent('likeUpdated', {
                        detail: { postId: id, isLiked: data.action === 'liked', action: data.action, newFavoriteCount: data.newFavoriteCount }
                    }));
                } else {
                    setIsLiked(data.action === 'liked');
                    setLikeCount(data.newFavoriteCount);
                }
            }
        } catch (error) {
            console.error('Like failed:', error);
        }
    };

    const handleClick = () => { if (!isPostMode) navigate(`/post?id=${id}`); };

    const handleAuthorClick = (e) => {
        e.stopPropagation();
        if (isSpacePost) navigate(`/gig/space/${space_id}`);
        else navigate(`/profile?userId=${author_id}&item=home`);
    };

    const isAuthor = isPostMode && loggedInUserId && author_id &&
        (loggedInUserId.toString() === author_id.toString());

    const handleEdit = () => {
        if (!isPostMode) return;
        const initValue = status === 'blog' ? 'blog' : 'comm';
        navigate(`/editpost?id=${id}&init=${initValue}`);
    };

    const handleDeleteClick = () => { if (!isPostMode) return; setShowDeleteConfirm(true); };

    const confirmDelete = async () => {
        if (!isPostMode) return;
        setShowDeleteConfirm(false);
        try {
            const { deleteJson } = await import('../../services/apiClient');
            const result = await deleteJson(`/api/post?id=${id}`);
            if (result.success) {
                setDeleteResultMessage('게시글이 삭제되었습니다.');
            } else {
                setDeleteResultMessage('삭제에 실패했습니다: ' + (result.error || '알 수 없는 오류'));
            }
            setShowDeleteResult(true);
        } catch (error) {
            setDeleteResultMessage('서버 오류가 발생했습니다.');
            setShowDeleteResult(true);
            console.error('Delete post error:', error);
        }
    };

    const handleDeleteResultConfirm = () => {
        setShowDeleteResult(false);
        if (deleteResultMessage === '게시글이 삭제되었습니다.') {
            navigate('/community');
            window.location.reload();
        }
    };

    const handleMenuAction = (action) => {
        setShowActionsMenu(false);
        if (action === 'share') setShowSharePopup(true);
        else if (action === 'save') onSaveToggle?.();
        else if (action === 'edit') handleEdit();
        else if (action === 'delete') handleDeleteClick();
        else if (action === 'select') onSelectToggle?.();
        else if (action === 'stats') setShowStatsPanel(v => !v);
    };

    // 액션 메뉴 아이템 목록
    const actionItems = isPostMode ? [
        { key: 'share', icon: 'share', label: '공유' },
        ...(authService.isLoggedIn() ? [{ key: 'save', icon: hasSaved ? 'bookmark' : 'bookmark_border', label: hasSaved ? '저장됨' : '저장', active: hasSaved }] : []),
        ...(isAdmin && typeof onSelectToggle === 'function' ? [{ key: 'select', icon: isSelected ? 'star' : 'star_border', label: isSelected ? '셀렉트 취소' : '셀렉트 지정', active: isSelected, loading: selectLoading }] : []),
        ...(isAuthor ? [
            { key: 'edit', icon: 'edit', label: '편집' },
            { key: 'delete', icon: 'delete', label: '삭제', danger: true },
            { key: 'stats', icon: 'bar_chart', label: '통계', active: showStatsPanel },
        ] : []),
    ] : [];

    return (
        <>
        <div
            className={isPostMode ? "box-col pd0 gap10" : "card-col gap10"}
            onClick={handleClick}
            style={{ cursor: isPostMode ? 'default' : 'pointer' }}
        >
            {/* 헤더 행: 아바타·이름·타임스탬프 */}
            <div className="box-row pd0">
                <div className="group-align-left">
                    {isSpacePost ? (
                        space_thumbnail ? (
                            <img
                                src={API_CONFIG.resolveFileUrl(space_thumbnail)}
                                className="img-profile-comment"
                                alt={space_title}
                                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                            />
                        ) : (
                            <div className="img-profile-comment" style={{ background: 'var(--color-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                                {(space_title || 'S').charAt(0).toUpperCase()}
                            </div>
                        )
                    ) : (
                        <img
                            src={author_profile_image ? API_CONFIG.resolveFileUrl(author_profile_image) : API_CONFIG.getDefaultAvatar(author_id)}
                            className="img-profile-comment"
                            alt="Profile"
                            onError={(e) => { e.target.onerror = null; e.target.src = API_CONFIG.getDefaultAvatar(author_id); }}
                        />
                    )}
                    <h4
                        style={{ cursor: isPostMode ? 'default' : 'pointer' }}
                        onClick={isPostMode ? undefined : handleAuthorClick}
                    >
                        {isSpacePost ? space_title : author_nickname}
                    </h4>
                    <span style={{ color: 'var(--color-monotone-4)' }}> · {formatPostTimestamp(timestamp)}</span>
                    {isSpacePost && visibility === 'space' && (
                        <span
                            className="badge-rtg badge-monotone-1"
                            style={{ fontSize: 11, fontWeight: 700, padding: '1px 8px', marginLeft: 6 }}
                        >
                            블로그
                        </span>
                    )}
                </div>
            </div>

            <h5>{title}</h5>
            {abstract && <p style={{ lineHeight: "1.4" }}>{abstract}</p>}
            {!hideMedia && (
                <div className="box-col pd0">
                    {(featured_image || first_image_url) && (
                        <img
                            src={API_CONFIG.resolveFileUrl(featured_image || first_image_url)}
                            className="img-feed"
                            alt=""
                            fetchPriority={imageFetchPriority}
                        />
                    )}
                </div>
            )}

            {/* 하단 바: 좋아요 + 조회수 + (post모드) ... 메뉴 */}
            <div className="box-row pd0">
                <div className="group-align-left">
                    {!isPostMode && <div className="btn-rtg btn-monotone-1">{category_name || category}</div>}
                    <div
                        className="btn-rtg btn-line"
                        onClick={handleLike}
                        style={{ cursor: 'pointer', color: isLiked ? 'var(--color-failure)' : 'inherit', borderColor: isLiked ? 'var(--color-failure)' : undefined }}
                    >
                        <span className="material-symbols-rounded" style={{ color: 'inherit' }}>favorite</span>
                        {likeCount}
                    </div>
                    <div className="btn-rtg btn-line" style={{ cursor: 'default' }}>
                        <span className="material-symbols-rounded">web_traffic</span>
                        {views || 0}
                    </div>

                    {/* Post 모드: ... 액션 메뉴 */}
                    {isPostMode && actionItems.length > 0 && (
                        <div
                            ref={actionsMenuRef}
                            style={{ position: 'relative' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                className="btn-sqr btn-line"
                                onClick={() => setShowActionsMenu(v => !v)}
                                aria-label="더 보기"
                            >
                                <span className="material-symbols-rounded">more_horiz</span>
                            </button>

                            {showActionsMenu && (
                                <div className="select-dropdown-menu post-action-dropdown-menu">
                                    {actionItems.map((item) => (
                                        <button
                                            key={item.key}
                                            type="button"
                                            onClick={() => handleMenuAction(item.key)}
                                            disabled={item.loading}
                                            className={[
                                                'select-dropdown-item',
                                                'post-action-dropdown-item',
                                                item.active ? 'selected' : '',
                                                item.danger ? 'danger' : '',
                                            ].filter(Boolean).join(' ')}
                                        >
                                            <span className="material-symbols-rounded">
                                                {item.icon}
                                            </span>
                                            {item.loading ? '처리 중...' : item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
        <hr />

        {/* 통계 팝업: 작성자 전용 */}
        {isPostMode && isAuthor && showStatsPanel && (
            <PostStatsPanel postId={id} onClose={() => setShowStatsPanel(false)} />
        )}

        {isPostMode && showDeleteConfirm && (
            <PopupAlert
                message="게시글을 삭제하시겠습니까?"
                onConfirm={confirmDelete}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        )}
        {isPostMode && showDeleteResult && (
            <PopupAlert
                message={deleteResultMessage}
                onConfirm={handleDeleteResultConfirm}
            />
        )}
        {isPostMode && (
            <PopupShare
                display={showSharePopup ? 'flex' : 'none'}
                onClose={() => setShowSharePopup(false)}
                postId={id}
                postTitle={title}
            />
        )}
        </>
    );
}

export default CardFeed;
